import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Modal,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Spinner,
  Card,
  TextField,
} from '@shopify/polaris';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import heic2any from 'heic2any';
import { generationsService } from '../../services/generationsService';
import { blobToBase64, blobToObjectURL, fetchImageBlob } from '../../utils/cropImage';

/**
 * Check if a URL points to a HEIC/HEIF image by extension
 */
const isHeicUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return pathname.endsWith('.heic') || pathname.endsWith('.heif');
  } catch {
    // If URL parsing fails, try simple string check
    const lower = url.toLowerCase();
    return lower.includes('.heic') || lower.includes('.heif');
  }
};

/**
 * Check if a blob is HEIC by its type or by inspecting magic bytes
 */
const isHeicBlob = (blob) => {
  const type = (blob.type || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence' || type === 'image/heif-sequence';
};

/**
 * Convert a HEIC blob to JPEG blob using heic2any
 */
const convertHeicToJpeg = async (heicBlob) => {
  const result = await heic2any({
    blob: heicBlob,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  // heic2any may return a single blob or an array
  return Array.isArray(result) ? result[0] : result;
};

/**
 * Fetch an image URL and convert to a displayable blob URL.
 * If the image is HEIC, converts to JPEG first.
 */
const fetchAndConvertIfHeic = async (imageUrl) => {
  let blob = await fetchImageBlob(imageUrl);

  // Check if it needs HEIC conversion (by URL extension or blob type)
  if (isHeicUrl(imageUrl) || isHeicBlob(blob)) {
    blob = await convertHeicToJpeg(blob);
  }

  return blob;
};

const KidImageSelectionModal = ({
  isOpen,
  onClose,
  generation,
  setToastMessage,
  setToastActive,
  onGenerationUpdated
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [kidImages, setKidImages] = useState([]);
  const [kidName, setKidName] = useState('');

  // HEIC conversion states
  const [convertedDisplayUrls, setConvertedDisplayUrls] = useState(new Map()); // Map of index -> blob URL (for HEIC images converted for display)
  const [convertingIndices, setConvertingIndices] = useState(new Set()); // Set of indices currently being converted

  // Crop functionality states
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [imageToCropIndex, setImageToCropIndex] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 50,
    aspect: 1
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImages, setCroppedImages] = useState(new Map()); // Map of index -> {blob, previewUrl, base64}
  const imgRef = useRef(null);

  // Extract kid images and name from generation data
  useEffect(() => {
    if (isOpen && generation) {
      const images = generation.kid_images || [];
      setKidImages(images);
      setKidName(generation.kid_name || '');
      setSelectedImageIndex(null);
    }
  }, [isOpen, generation]);

  // Convert HEIC images for display when kidImages changes
  useEffect(() => {
    if (!isOpen || kidImages.length === 0) return;

    // Find HEIC images that need conversion
    kidImages.forEach((imageUrl, index) => {
      if (isHeicUrl(imageUrl) && !convertedDisplayUrls.has(index)) {
        // Mark as converting
        setConvertingIndices(prev => {
          const next = new Set(prev);
          next.add(index);
          return next;
        });

        // Convert in background
        fetchAndConvertIfHeic(imageUrl)
          .then((jpegBlob) => {
            const displayUrl = URL.createObjectURL(jpegBlob);
            setConvertedDisplayUrls(prev => {
              const next = new Map(prev);
              next.set(index, displayUrl);
              return next;
            });
          })
          .catch((err) => {
            console.error(`Failed to convert HEIC image at index ${index}:`, err);
          })
          .finally(() => {
            setConvertingIndices(prev => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });
          });
      }
    });
  }, [isOpen, kidImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedImageIndex(null);
      setKidImages([]);
      setKidName('');
      croppedImages.forEach(({ previewUrl }) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      });
      setCroppedImages(new Map());
      setShowCropDialog(false);
      // Revoke converted display URLs to free memory
      convertedDisplayUrls.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      setConvertedDisplayUrls(new Map());
      setConvertingIndices(new Set());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageSelect = useCallback((index) => {
    setSelectedImageIndex(index);
  }, []);

  // Open crop dialog for an image (handles HEIC conversion)
  const handleCropImage = useCallback(async (imageUrl, index, e) => {
    e.stopPropagation(); // Prevent image selection

    try {
      // Fetch and convert if HEIC
      const blob = await fetchAndConvertIfHeic(imageUrl);
      const objectUrl = URL.createObjectURL(blob);

      setImageToCrop(objectUrl);
      setImageToCropIndex(index);
      setCrop({
        unit: '%',
        width: 50,
        aspect: 1
      });
      setCompletedCrop(null);
      setShowCropDialog(true);
    } catch (error) {
      console.error('Failed to load image for cropping:', error);
      setToastMessage('Failed to load image. Please try again.');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    }
  }, [setToastMessage, setToastActive]);

  // Create cropped image from canvas
  const getCroppedImg = useCallback((image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  }, []);

  // Save the cropped image
  const handleSaveCrop = useCallback(async () => {
    try {
      if (!completedCrop || !imgRef.current) {
        setToastMessage('Please adjust the crop area first');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 2000);
        return;
      }

      // Create cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);

      // Create preview URL
      const previewUrl = blobToObjectURL(croppedBlob);

      // Convert to base64 for sending to backend
      const base64 = await blobToBase64(croppedBlob);

      // Store cropped image data
      setCroppedImages(prev => {
        const newMap = new Map(prev);
        const previousCrop = newMap.get(imageToCropIndex);
        if (previousCrop?.previewUrl) {
          URL.revokeObjectURL(previousCrop.previewUrl);
        }
        newMap.set(imageToCropIndex, { blob: croppedBlob, previewUrl, base64 });
        return newMap;
      });

      // Clean up the crop dialog object URL
      if (imageToCrop && imageToCrop.startsWith('blob:')) {
        URL.revokeObjectURL(imageToCrop);
      }

      // Close crop dialog
      setShowCropDialog(false);
      setImageToCrop(null);
      setImageToCropIndex(null);

      // Auto-select the cropped image
      setSelectedImageIndex(imageToCropIndex);

      setToastMessage('Image cropped successfully');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    } catch (error) {
      console.error('Crop error:', error);
      setToastMessage('Failed to crop image');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    }
  }, [completedCrop, imageToCrop, imageToCropIndex, getCroppedImg, setToastMessage, setToastActive]);

  // Cancel crop
  const handleCancelCrop = useCallback(() => {
    // Clean up object URL if it exists
    if (imageToCrop && imageToCrop.startsWith('blob:')) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropDialog(false);
    setImageToCrop(null);
    setImageToCropIndex(null);
    setCrop({
      unit: '%',
      width: 50,
      aspect: 1
    });
    setCompletedCrop(null);
  }, [imageToCrop]);

  // Remove crop for an image
  const handleRemoveCrop = useCallback((index, e) => {
    e.stopPropagation();
    setCroppedImages(prev => {
      const newMap = new Map(prev);
      // Revoke object URL to free memory
      const cropped = newMap.get(index);
      if (cropped?.previewUrl) {
        URL.revokeObjectURL(cropped.previewUrl);
      }
      newMap.delete(index);
      return newMap;
    });
    setToastMessage('Crop removed');
    setToastActive(true);
    setTimeout(() => setToastActive(false), 2000);
  }, [setToastMessage, setToastActive]);

  const handleGenerate = useCallback(async () => {
    if (selectedImageIndex === null || !generation) {
      setToastMessage('Please select a kid image first');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    if (!kidName.trim()) {
      setToastMessage('Please enter a kid name');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsGenerating(true);
    try {
      const selectedImage = kidImages[selectedImageIndex];
      const croppedImageData = croppedImages.get(selectedImageIndex);

      let kidImage;
      if (croppedImageData) {
        // Use cropped image base64
        kidImage = croppedImageData.base64;
      } else if (isHeicUrl(selectedImage)) {
        // For uncropped HEIC images, convert to JPEG base64 before sending
        const jpegBlob = await fetchAndConvertIfHeic(selectedImage);
        kidImage = await blobToBase64(jpegBlob);
      } else {
        // Use original URL for non-HEIC images
        kidImage = selectedImage;
      }

      // Call generateFace to start the face generation first (two-phase flow)
      // The face will be generated, then user reviews it before scene generation
      await generationsService.generateFace(generation.generation_id, kidImage, kidName);

      setToastMessage('Face generation started! You will be able to review it before continuing.');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);

      onClose();

      // Notify parent component to refresh generations list
      if (onGenerationUpdated) {
        onGenerationUpdated();
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to start face generation';
      setToastMessage(`Error: ${errorMessage}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedImageIndex, generation, kidImages, croppedImages, kidName, setToastMessage, setToastActive, onClose, onGenerationUpdated]);

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        title={<div style={{ padding: '16px' }}>{`Select Kid Image`}</div>}
        primaryAction={{
          content: 'Generate Face',
          onAction: handleGenerate,
          loading: isGenerating,
          disabled: selectedImageIndex === null || isGenerating || !kidName.trim()
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: onClose,
            disabled: isGenerating
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Kid Name"
              value={kidName}
              onChange={setKidName}
              placeholder="Enter kid's name"
              autoComplete="off"
            />

            <Text variant="bodyMd">
              Select one of the kid images below to start the generation process:
            </Text>

            {kidImages.length === 0 ? (
              <Card>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Text variant="bodyMd" tone="subdued">
                    No kid images available for this generation.
                  </Text>
                </div>
              </Card>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '16px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {kidImages.map((imageUrl, index) => {
                  const croppedImageData = croppedImages.get(index);
                  const isConverting = convertingIndices.has(index);
                  const convertedUrl = convertedDisplayUrls.get(index);
                  // Priority: cropped > converted (HEIC->JPEG) > original URL
                  const displayUrl = croppedImageData
                    ? croppedImageData.previewUrl
                    : (convertedUrl || imageUrl);
                  const isCropped = !!croppedImageData;
                  const isHeic = isHeicUrl(imageUrl);

                  return (
                    <div
                      key={index}
                      onClick={() => handleImageSelect(index)}
                      style={{
                        position: 'relative',
                        cursor: 'pointer',
                        border: selectedImageIndex === index ? '3px solid #008060' : '2px solid #e1e3e5',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease-in-out',
                        transform: selectedImageIndex === index ? 'scale(1.02)' : 'scale(1)',
                        boxShadow: selectedImageIndex === index ? '0 4px 12px rgba(0, 128, 96, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {/* Show spinner while converting HEIC */}
                      {isConverting && !croppedImageData ? (
                        <div style={{
                          width: '100%',
                          height: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f6f6f7',
                          gap: '8px'
                        }}>
                          <Spinner size="small" />
                          <Text variant="bodySm" tone="subdued">Converting HEIC...</Text>
                        </div>
                      ) : (
                        <img
                          src={displayUrl}
                          alt={`Kid image ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.src = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                          }}
                        />
                      )}

                      {/* Crop/Remove Crop Buttons */}
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        right: '8px',
                        display: 'flex',
                        gap: '4px',
                        justifyContent: 'center'
                      }}>
                        <button
                          onClick={(e) => handleCropImage(imageUrl, index, e)}
                          style={{
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            flex: 1
                          }}
                          title={isCropped ? 'Re-crop image' : 'Crop image'}
                        >
                          {isCropped ? '✂️ Re-crop' : '✂️ Crop'}
                        </button>
                        {isCropped && (
                          <button
                            onClick={(e) => handleRemoveCrop(index, e)}
                            style={{
                              background: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                            title="Remove crop"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {/* HEIC Badge */}
                      {isHeic && !isCropped && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          background: '#6366f1',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          HEIC
                        </div>
                      )}

                      {/* Cropped Badge */}
                      {isCropped && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          background: '#008060',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          CROPPED
                        </div>
                      )}

                      {/* Selected Badge */}
                      {selectedImageIndex === index && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: '#008060',
                          color: 'white',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Crop Dialog Modal - Rendered outside using Portal */}
      {showCropDialog && imageToCrop && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text variant="headingMd" as="h3" style={{ color: 'white' }}>
              Crop Image
            </Text>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button onClick={handleCancelCrop} variant="primary" tone="critical">
                Cancel
              </Button>
              <Button onClick={handleSaveCrop} variant="primary" tone="success">
                Save Crop
              </Button>
            </div>
          </div>

          {/* Cropper Area */}
          <div style={{
            flex: 1,
            position: 'relative',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflow: 'auto'
          }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              style={{
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            >
              <img
                ref={imgRef}
                src={imageToCrop}
                alt="Crop preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 300px)',
                  objectFit: 'contain'
                }}
              />
            </ReactCrop>
          </div>

          {/* Instructions */}
          <div style={{
            padding: '20px 24px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{
              maxWidth: '600px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <Text variant="bodyMd" as="p" style={{ color: '#e0e0e0' }}>
                💡 <strong>Drag the corners or edges</strong> to resize the crop area
              </Text>
              <Text variant="bodySm" as="p" style={{ color: '#999' }}>
                Drag inside the crop box to reposition it
              </Text>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default KidImageSelectionModal;